
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, ArrowLeft } from "lucide-react";
import { getMessages, sendMessage, subscribeToConversation, Message, Recipient } from "@/utils/chatService";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { format, isToday, isYesterday, isThisWeek, formatDistanceToNow } from "date-fns";
import ServiceHeader from "@/components/ServiceHeader";
import PriceProposalMessage from "@/components/PriceProposalMessage";
import MessageInputBar from "@/components/MessageInputBar";
import { supabase } from "@/integrations/supabase/client";

interface GroupedMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: Date;
  isLastInGroup: boolean;
  isFirstInGroup: boolean;
}

interface MessageGroup {
  senderId: string;
  timestamp: Date;
  messages: GroupedMessage[];
}

interface ConversationData {
  id: string;
  participant1_id: string;
  participant2_id: string;
  service_id?: string;
  service_started?: boolean;
  service_completed?: boolean;
  final_price?: number | null;
  price_agreed_by_non_posting_party?: boolean;
  price_confirmed_by_provider?: boolean;
  payment_completed?: boolean;
  latest_proposal_price?: number | null;
  latest_proposal_pricing_type?: string | null;
  latest_proposal_by?: string | null;
  services?: { title: string; price: number; pricing_type: string; user_id: string } | null;
}

const Chat = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOpen } = useSidebar();
  const [messages, setMessages] = useState<Message[]>([]);
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (instant = false) => {
    if (messagesEndRef.current) {
      // Use scrollIntoView with block: 'nearest' to prevent aggressive scrolling
      const options = instant 
        ? { behavior: "auto" as const, block: "nearest" as const }
        : { behavior: "smooth" as const, block: "nearest" as const };
      
      messagesEndRef.current.scrollIntoView(options);
    }
  };

  // Helper function to detect system messages
  const isSystemMessage = (text: string): boolean => {
    return text.startsWith('[SYSTEM]') || 
           text.startsWith('[PRICE_PROPOSAL]') || 
           text.startsWith('[PRICE_ACCEPTED]') || 
           text.startsWith('[PRICE_REJECTED]');
  };

  // Render system message component
  const renderSystemMessage = (message: Message) => {
    const messageText = message.text;
    const messageId = typeof message.id === 'string' ? message.id : message.id.toString();
    
    if (messageText.startsWith('[PRICE_PROPOSAL]')) {
      return (
        <div key={`system-${messageId}`} className="w-full flex justify-center my-4">
          <PriceProposalMessage
            message={messageText}
            senderId={message.senderId}
            conversationId={conversationId!}
            onUpdate={loadConversation}
          />
        </div>
      );
    }
    
    if (messageText.startsWith('[PRICE_ACCEPTED]') || messageText.startsWith('[PRICE_REJECTED]')) {
      const isAccepted = messageText.startsWith('[PRICE_ACCEPTED]');
      const priceMatch = messageText.match(/\$(\d+(?:\.\d{2})?)(\/hour|\/job)?/);
      const price = priceMatch ? priceMatch[1] : '';
      const pricingType = priceMatch && priceMatch[2] ? priceMatch[2] : '';
      
      return (
        <div key={`system-${messageId}`} className="w-full flex justify-center my-4">
          <div
            className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
              isAccepted 
                ? 'bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-900 dark:text-green-100' 
                : 'bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-900 dark:text-red-100'
            }`}
          >
            <p className="text-sm font-medium text-center">
              {isAccepted ? `✓ Price agreed: $${price}${pricingType}` : `✗ Price rejected: $${price}${pricingType}`}
            </p>
          </div>
        </div>
      );
    }
    
    if (messageText.startsWith('[SYSTEM]')) {
      return (
        <div key={`system-${messageId}`} className="w-full flex justify-center my-4">
          <div className="max-w-xs lg:max-w-md px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-foreground">
            <p className="text-sm font-medium text-center">
              {messageText.replace('[SYSTEM] ', '')}
            </p>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Group messages by sender and time proximity (excluding system messages)
  const groupMessages = (messages: Message[]): MessageGroup[] => {
    // Filter out system messages from grouping
    const regularMessages = messages.filter(msg => !isSystemMessage(msg.text));
    
    if (regularMessages.length === 0) return [];

    const groups: MessageGroup[] = [];
    let currentGroup: MessageGroup | null = null;

    regularMessages.forEach((message, index) => {
      // Ensure timestamp is a Date object
      const messageTimestamp = message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp);
      
      const shouldStartNewGroup = !currentGroup || 
        currentGroup.senderId !== message.senderId ||
        (messageTimestamp.getTime() - currentGroup.timestamp.getTime()) > 5 * 60 * 1000; // 5 minutes

      if (shouldStartNewGroup) {
        currentGroup = {
          senderId: message.senderId,
          timestamp: messageTimestamp,
          messages: []
        };
        groups.push(currentGroup);
      }

      const nextMessage = regularMessages[index + 1];
      const nextMessageTimestamp = nextMessage ? 
        (nextMessage.timestamp instanceof Date ? nextMessage.timestamp : new Date(nextMessage.timestamp)) : 
        null;

      const isLastInGroup = index === regularMessages.length - 1 || 
        nextMessage?.senderId !== message.senderId ||
        (nextMessageTimestamp && (nextMessageTimestamp.getTime() - messageTimestamp.getTime()) > 5 * 60 * 1000);

      const isFirstInGroup = currentGroup.messages.length === 0;

      currentGroup.messages.push({
        id: message.id.toString(),
        senderId: message.senderId,
        text: message.text,
        timestamp: messageTimestamp,
        isLastInGroup,
        isFirstInGroup
      });
    });

    return groups;
  };

  const formatTimestamp = (timestamp: Date): string => {
    if (isToday(timestamp)) {
      return format(timestamp, 'h:mm a');
    } else if (isYesterday(timestamp)) {
      return `Yesterday ${format(timestamp, 'h:mm a')}`;
    } else if (isThisWeek(timestamp)) {
      return format(timestamp, 'EEE h:mm a');
    } else {
      return format(timestamp, 'MMM d, h:mm a');
    }
  };

  const shouldShowTimestamp = (groups: MessageGroup[], groupIndex: number): boolean => {
    if (groupIndex === 0) return true;
    
    const currentGroup = groups[groupIndex];
    const previousGroup = groups[groupIndex - 1];
    
    // Show timestamp if more than 30 minutes between groups
    return (currentGroup.timestamp.getTime() - previousGroup.timestamp.getTime()) > 30 * 60 * 1000;
  };

  useEffect(() => {
    console.log('Chat component mounted, user:', user?.id, 'conversationId:', conversationId);
    
    if (!user) {
      navigate("/auth");
      return;
    }
    if (conversationId) {
      loadMessages();
      loadConversation();
    }
  }, [conversationId, user, navigate]);

  useEffect(() => {
    if (!conversationId) return;
    
    console.log('Setting up message subscription for conversation:', conversationId);
    
    const unsubscribe = subscribeToConversation(conversationId, (newMessage) => {
      console.log('Received new message:', newMessage);
      setMessages(prev => [...prev, newMessage]);

      // Keep header state (Pay/Confirm) in sync by reloading conversation when price-related system messages arrive.
      if (newMessage.text.startsWith('[PRICE_')) {
        loadConversation();
      }

      // Only scroll for non-system messages to prevent layout shifts
      if (!newMessage.text.startsWith('[PRICE_') && !newMessage.text.startsWith('[SYSTEM]')) {
        setTimeout(() => scrollToBottom(false), 100);
      }
    });

    // Subscribe to conversation updates for real-time state changes
    const conversationChannel = supabase
      .channel(`chat-conversation-updates-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `id=eq.${conversationId}`
        },
        (payload) => {
          console.log('Received conversation update:', payload);
          loadConversation(); // Reload conversation data
        }
      )
      .subscribe();
    
    return () => {
      unsubscribe();
      supabase.removeChannel(conversationChannel);
    };
  }, [conversationId]);

  const loadConversation = async () => {
    if (!conversationId) return;
    
    try {
      console.log('Loading conversation data for:', conversationId);
      
      const { data: convo, error } = await supabase
        .from('conversations')
        .select('id, participant1_id, participant2_id, service_id, service_started, service_completed, final_price, price_agreed_by_non_posting_party, price_confirmed_by_provider, payment_completed, latest_proposal_price, latest_proposal_pricing_type, latest_proposal_by')
        .eq('id', conversationId)
        .single();

      if (error || !convo) {
        console.error('Conversation fetch error:', error);
        setAccessDenied(true);
        return;
      }

      let serviceData = null;
      if (convo.service_id) {
        const { data: service, error: serviceErr } = await supabase
          .from('services')
          .select('title, price, pricing_type, user_id')
          .eq('id', convo.service_id)
          .single();

        if (!serviceErr && service) {
          serviceData = service;
        }
      }

      setConversation({
        ...convo,
        services: serviceData
      });
    } catch (error) {
      console.error("Failed to load conversation:", error);
    }
  };

  const loadMessages = async () => {
    if (!conversationId) return;
    
    try {
      setIsLoading(true);
      console.log('Loading messages for conversation:', conversationId);
      const { messages: loadedMessages, recipient: loadedRecipient } = await getMessages(conversationId);
      console.log('Loaded messages:', loadedMessages.length, 'recipient:', loadedRecipient);
      setMessages(loadedMessages);
      setRecipient(loadedRecipient);
    } catch (error) {
      console.error("Failed to load messages:", error);
      const raw = JSON.stringify(error || {}).toLowerCase();
      if (raw.includes("unauthorized") || raw.includes("permission") || raw.includes("row level security") || raw.includes("rls") || raw.includes("not allowed")) {
        setAccessDenied(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewMessage = async (messageText: string) => {
    if (!recipient) throw new Error("No recipient found");
    
    console.log('Sending message to:', recipient.id, 'text:', messageText);
    await sendMessage({
      senderId: user!.id,
      receiverId: recipient.id,
      text: messageText
    });
    
    console.log('Message sent successfully');
  };

  if (!user) {
    return null;
  }

  if (accessDenied) {
    return (
      <div className="h-full flex justify-center items-center">
        <p className="text-muted-foreground">You don't have access to this conversation.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex justify-center items-center">
        <p className="text-muted-foreground">Loading conversation...</p>
      </div>
    );
  }

  const messageGroups = groupMessages(messages);
  
  // Sort all messages chronologically
  const sortedMessages = [...messages].sort((a, b) => {
    const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
    const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
    return aTime - bTime;
  });

  return (
    <div className="h-screen w-full flex flex-col bg-background overflow-hidden pt-3">
      {/* Header - Fixed height with proper top spacing */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3">
        <Button 
          variant="outline" 
          onClick={() => navigate("/conversations")}
          className="mb-3"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Conversations
        </Button>
        
        {recipient && (
          <ServiceHeader
            recipient={recipient}
            conversation={conversation}
            isProvider={user?.id === conversation?.participant2_id}
            isClient={user?.id === conversation?.participant1_id}
            onUpdate={loadConversation}
          />
        )}
      </div>

      {/* Chat container - Flexible height between header and input */}
      <div className="flex-1 flex flex-col min-h-0 bg-background relative overflow-hidden">
        {/* Enhanced pattern backdrop */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10 pointer-events-none z-0">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='currentColor' fill-opacity='0.3' fill-rule='evenodd'%3E%3Cpath d='m0 60l60-60h-60v60zm60 0v-60h-60l60 60z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px',
              backgroundRepeat: 'repeat'
            }}
          />
        </div>

        {/* Messages area - Flexible height with stable bottom padding for fixed input */}
        <div 
          className="flex-1 px-4 overflow-y-auto relative z-10 min-h-0" 
          style={{ paddingBottom: '180px' }}
          data-messages-container
        >
          {messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {sortedMessages.map((message, index) => {
                // Check if this is a system message
                if (isSystemMessage(message.text)) {
                  return renderSystemMessage(message);
                }
                
                // Regular message rendering logic with grouping
                const messageTime = message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp);
                const isFromUser = message.senderId === user.id;
                
                return (
                  <div key={message.id} className={`flex ${isFromUser ? 'justify-end' : 'justify-start'} mb-1`}>
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl ${
                        isFromUser
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-muted text-foreground border border-border shadow-sm'
                      } transition-all duration-200 hover:shadow-lg relative`}
                    >
                      <p className="font-inter text-sm leading-relaxed">{message.text}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>
      
      {/* Message Input Bar - Fixed to bottom, respecting sidebar */}
      <div className={`fixed bottom-0 left-0 z-50 bg-background border-t border-border transition-all duration-300 ease-in-out ${isOpen ? 'w-[calc(100vw-80px)]' : 'w-full'}`}>
        <MessageInputBar 
          onSendMessage={handleNewMessage} 
          disabled={!recipient}
          placeholder="Type your message..."
        />
      </div>
    </div>
  );
};

export default Chat;
